<!-- eslint-disable vue/no-lone-template -->
<!--
  File name: FinalBreachEvaluationForm.vue
  Program Description:
    - This component contains the action buttons that can be performed in each FBNER file
    - Each action button have display restrictions based on the user roles/s of the logged in user

  Includes:
    - FBNERView: rendered when this.operation === view
    - FBNERCreate: rendered for creating FBNER or editing FBNER
    - ViewFilesModal: used for viewing uploaded files
-->
<template>
  <v-container>
    <v-row
      align-self="start"
      justify="center"
    >
      <v-col>
        <v-card
          :key="operation"
          class="transparent-bg"
          tile
          elevation="0"
        >
          <v-card-title class="pl-0 pb-0">
            <h2>
              <b> {{ formItem.dbnNumber }} - FBNER <span v-if="roleView==='drafts'">Draft</span></b>
            </h2>
            <v-spacer />
            <v-breadcrumbs
              large
              :items="breadcrumbs.items"
              :divider="breadcrumbs.divider"
            />
          </v-card-title>
        </v-card>
      </v-col>
    </v-row>
    <v-row>
      <v-col />
    </v-row>
    <v-row
      v-if="roleView !== 'drafts'"
      align-self="start"
      justify="center"
    >
      <v-col class="mb-0 pb-0">
        <v-card
          :key="operation"
          tile
          elevation="0"
          outlined
        >
          <v-card-title class="pt-2 pb-2">
            <v-btn
              v-if="operation === 'create' || operation === 'update'"
              id="cancel-btn"
              color="grey darken-1"
              text
              @click="back()"
            >
              Cancel
            </v-btn>
            <template v-if="operation === 'view' && !isSubmitted && forEvaluation">
              <v-btn
                v-show="isDraft && showLabel(`canPatchFbner`)"
                id="draft-back-btn"
                text
                :loading="loading"
                @click="() => {
                  isDraft = false
                  $router.replace({
                    params: {
                      role: 'drafts',
                      id: formItem.pdbnf._id ? formItem.pdbnf._id : formItem.pdbnf
                    }
                  })
                  !drafts && getDrafts(formItem.pdbnf._id ? formItem.pdbnf._id : formItem.pdbnf)
                }
                "
              >
                <v-icon>mdi-arrow-left</v-icon>
                {{ getLabel(`canPatchFbner`, 'Back to Draft ') }}
              </v-btn>
            </template>
            <template v-if="operation === 'view' && !isDraft">
              <v-btn
                v-if="!loading"
                id="back-btn"
                text
                :loading="loading"
                @click=" $router.push({
                  name: 'DataBreachView',
                  params: {
                    dataTab: 'pdbnf',
                    operation: 'view',
                    id: formItem.pdbnf._id ? formItem.pdbnf._id : formItem.pdbnf
                  }
                })"
              >
                <v-icon>mdi-arrow-left</v-icon>
                Back to {{ formItem.dbnNumber }}
              </v-btn>
            </template>

            <v-spacer />
            <template v-if="isPdbnfValid">
              <template v-if="operation !== 'view'">
                <ViewFilesModal
                  :form-item="viewDocData || formItem"
                  :loading="loading"
                />
              </template>
              <template v-if="operation !== 'view' && !formItem.isSubmitted && forEvaluation">
                <v-btn
                  v-if="showLabel(`canPatchFbner`)"
                  id="save-draft-btn"
                  color="primary"
                  text
                  type="button"
                  :loading="loading"
                  @click="confirmDialog(false)"
                >
                  {{ getLabel(`canPatchFbner`, 'Save as Draft') }}
                </v-btn>
                <v-btn
                  v-if=" showLabel(`canPostFbner`)"
                  id="submit-btn"
                  color="primary"
                  type="button"
                  :loading="loading"
                  @click="confirmDialog(true)"
                >
                  {{ getLabel(`canPostFbner`, 'Submit FBNER') }}
                </v-btn>
              </template>
              <template v-if="operation === 'view' && !isDraft && roleView === 'default' && !isPic">
                <v-btn
                  v-if="showLabel(`canPatchFbner`)"
                  id="view-drafts-btn"
                  class="mr-1"
                  color="primary"
                  type="button"
                  :loading="loading"
                  @click="$router.replace({params: {role: 'drafts'}})"
                >
                  <v-icon class="mr-2">
                    mdi-pencil
                  </v-icon>
                  {{ getLabel(`canPatchFbner`, 'Draft' ) }}
                </v-btn>
                <v-btn
                  v-if="showLabel(`canPostFbner`) && !drafts.length"
                  id="initiate-btn"
                  color="primary"
                  type="button"
                  :loading="loading"
                  @click="$router.replace({params: { operation: 'create'} }); readOnly = false"
                >
                  <v-icon class="mr-2">
                    mdi-file-plus
                  </v-icon>
                  {{ getLabel(`canPostFbner`, 'Initiate FBNER' ) }}
                </v-btn>
              </template>
              <template v-if="operation === 'view' && !isSubmitted && forEvaluation">
                <v-btn
                  v-show="isDraft && showLabel(`canPatchFbner`)"
                  id="edit-btn"
                  color="primary"
                  :loading="loading"
                  @click="$router.replace({params: { operation: 'update'} })"
                >
                  <v-icon class="mr-2">
                    mdi-pencil
                  </v-icon>
                  {{ getLabel(`canPatchFbner`, 'Edit Draft') }}
                </v-btn>
              </template>
              <!-- REVIEWER -->
              <template v-if="operation === 'view' && (initialReviewer || finalReviewer)">
                <v-btn
                  v-show="showLabel(`canPatchFbner`)"
                  id="edit-btn"
                  color="primary"
                  :loading="loading"
                  @click="$router.replace({params: { operation: 'update'} })"
                >
                  <v-icon class="mr-2">
                    mdi-file-search
                  </v-icon>
                  {{ getLabel(`canPatchFbner`, 'Review') }}
                </v-btn>
              </template>
              <!-- FIRST PASS -->
              <template v-if="operation !== 'view' && formItem.isSubmitted && forReview && initialReviewer">
                <v-btn
                  v-if="showLabel(`canPatchFbner`)"
                  id="submit-btn"
                  color="primary"
                  type="button"
                  :loading="loading"
                  @click="approveFbner('Review')"
                >
                  {{ getLabel(`canPatchFbner`, 'Submit FBNER') }}
                </v-btn>
              </template>
              <!-- FINAL -->
              <template v-if="operation !== 'view' && formItem.isSubmitted && finalReviewer">
                <v-btn
                  v-if="showLabel(`canPatchFbner`)"
                  id="submit-btn"
                  color="primary"
                  type="button"
                  :loading="loading"
                  @click="isForEndorsement = true"
                >
                  {{ getLabel(`canPatchFbner`, 'Approve FBNER') }}
                </v-btn>
              </template>
              <!-- ENDORSE -->
              <template v-if="operation !== 'view' && formItem.isSubmitted && (assignedAdjudicator || assignedInvestigator)">
                <v-btn
                  v-if="showLabel(`canPatchFbner`)"
                  id="submit-btn"
                  color="primary"
                  type="button"
                  :loading="loading"
                  @click="isForEndorsement = true"
                >
                  {{ getLabel(`canPatchFbner`, 'Endorse') }}
                </v-btn>
              </template>
              <!-- ADJUDICATION -->
              <template v-if="operation === 'view' && assignedAdjudicator">
                <v-btn
                  v-show="showLabel(`canPatchFbner`)"
                  id="endorse-btn"
                  color="primary"
                  :loading="loading"
                  @click="$router.replace({params: { operation: 'update'} })"
                >
                  {{ getLabel(`canPatchFbner`, 'Update FBNER') }}
                </v-btn>
              </template>
              <!-- INVESTIGATION -->
              <template v-if="operation === 'view' && assignedInvestigator">
                <v-btn
                  v-show="showLabel(`canPatchFbner`)"
                  id="endorse-btn"
                  color="primary"
                  :loading="loading"
                  @click="$router.replace({params: { operation: 'update'} })"
                >
                  {{ getLabel(`canPatchFbner`, 'Investigate') }}
                </v-btn>
              </template>
              <!-- ENFORCEMENT -->
              <template v-if="operation === 'view' && assignedEnforcer">
                <v-btn
                  v-show="showLabel(`canPatchFbner`)"
                  id="endorse-btn"
                  color="primary"
                  :loading="loading"
                  @click="$router.replace({params: { operation: 'update'} })"
                >
                  {{ getLabel(`canPatchFbner`, 'Update FBNER') }}
                </v-btn>
              </template>
              <!-- CLOSING -->
              <template v-if="operation !== 'view' && formItem.isSubmitted && assignedEnforcer">
                <v-btn
                  v-if="showLabel(`canPatchFbner`)"
                  id="submit-btn"
                  color="primary"
                  type="button"
                  :loading="loading"
                  @click="transmitFbner(process)"
                >
                  {{ getLabel(`canPatchFbner`, 'Done') }}
                </v-btn>
              </template>

              <v-dialog
                v-model="isForEndorsement"
                max-width="600"
                content-class="dialog"
                persistent
              >
                <v-card
                  tile
                  style="padding:10px;"
                  max-height="50vh"
                >
                  <template v-if="finalReviewer">
                    <v-card-title
                      class="headline primary--text"
                    >
                      Endorse FBNER
                    </v-card-title>

                    <div style="padding: 20px;">
                      <VAutocomplete
                        id="endorse-to-field"
                        v-model="endorsedTo"
                        label="Endorse To"
                        is-required
                        outlined
                        dense
                        :items="endorseItems"
                        :error-messages="genericError('endorsedTo', 'Endorsed To')"
                        @input="$v.endorsedTo.$touch()"
                        @blur="$v.endorsedTo.$touch()"
                      />
                      <VAutocomplete
                        id="endorse-to-field"
                        v-model="endorseToUser"
                        label="Users"
                        placeholder="Select user..."
                        is-required
                        outlined
                        item-text="fullName"
                        item-value="_id"
                        dense
                        :items="endorseItemsUsers"
                        :error-messages="genericError('endorseToUser', 'User')"
                        @input="$v.endorseToUser.$touch()"
                        @blur="$v.endorseToUser.$touch()"
                      />
                    </div>
                  </template>
                  <template v-if="assignedAdjudicator || assignedInvestigator">
                    <v-card-title
                      class="headline primary--text"
                    >
                      Endorse to {{ nextEndorseStage }}
                    </v-card-title>
                    <div style="padding: 20px;">
                      <VAutocomplete
                        id="endorse-to-field"
                        v-model="endorseToUser"
                        label="Users"
                        placeholder="Select user..."
                        is-required
                        outlined
                        item-text="fullName"
                        item-value="_id"
                        dense
                        :items="endorseSecondStageUsers"
                        :error-messages="genericError('endorseToUser', 'User')"
                        @input="$v.endorseToUser.$touch()"
                        @blur="$v.endorseToUser.$touch()"
                      />
                    </div>
                  </template>
                  <v-card-actions>
                    <v-spacer />
                    <v-btn
                      text
                      @click="closeEndorseDialog"
                    >
                      Cancel
                    </v-btn>
                    <v-btn
                      class="px-6"
                      color="primary"
                      :loading="loading"
                      @click="transmitFbner(process, endorseToUser)"
                    >
                      Endorse
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
            </template>
          </v-card-title>
        </v-card>
      </v-col>
    </v-row>
    <v-row
      align-self="start"
      justify="center"
    >
      <v-col class="mt-0 pt-0">
        <v-card
          :key="operation"
          tile
          elevation="0"
          outlined
        >
          <template v-if="roleView !== 'drafts'">
            <v-card-text class="pt-0">
              <v-container>
                <v-row>
                  <v-col class="px-2 pb-10 pt-4">
                    <h1 class="title primary--text">
                      Final Breach Notification Evaluation Report
                    </h1>
                  </v-col>
                </v-row>
                <!-- read only  -->
                <FBNERView
                  v-if="operation === 'view' && roleView !== 'drafts'"
                  ref="FBNERView"
                />
                <!-- create -->
                <FBNERCreate
                  v-if="operation !== 'view' && roleView !== 'drafts'"
                  ref="FBNERCreate"
                  @emitDraft="showDraft"
                  @loading="isLoading => loading = isLoading"
                />
              </v-container>
            </v-card-text>
          </template>
          <v-card-text>
            <v-row v-if="isPdbnfValid">
              <v-btn
                v-if="operation === 'create' || operation === 'update'"
                id="cancel-btn"
                color="black"
                text
                @click="back()"
              >
                Cancel
              </v-btn>
              <template v-if="roleView === 'drafts'">
                <v-btn
                  color="primary"
                  text
                  @click="
                    $router.replace({
                      params:
                        {
                          role: 'default',
                          id: formItem.pdbnf._id ? formItem.pdbnf._id : formItem.pdbnf
                        }
                    })"
                >
                  <v-icon>mdi-arrow-left</v-icon>
                  Back to FBNER
                </v-btn>
              </template>
              <v-spacer />
              <template v-if="operation !== 'view'">
                <ViewFilesModal
                  :form-item="viewDocData || formItem"
                  :loading="loading"
                />
              </template>
              <template v-if="operation !== 'view' && !formItem.isSubmitted && forEvaluation">
                <v-btn
                  v-if="showLabel(`canPatchFbner`)"
                  id="save-draft-btn"
                  color="primary"
                  text
                  type="button"
                  :loading="loading"
                  @click="confirmDialog(false)"
                >
                  {{ getLabel(`canPatchFbner`, 'Save as Draft') }}
                </v-btn>
                <v-btn
                  v-if=" showLabel(`canPostFbner`)"
                  id="submit-btn"
                  color="primary"
                  type="button"
                  :loading="loading"
                  @click="confirmDialog(true)"
                >
                  {{ getLabel(`canPostFbner`, 'Submit FBNER') }}
                </v-btn>
              </template>
              <template v-if="operation === 'view' && !isDraft && roleView === 'default' && !isPic">
                <v-btn
                  v-if="showLabel(`canPatchFbner`)"
                  id="view-drafts-btn"
                  class="mr-1"
                  color="primary"
                  type="button"
                  :loading="loading"
                  @click="$router.replace({params: {role: 'drafts'}})"
                >
                  <v-icon class="mr-2">
                    mdi-pencil
                  </v-icon>
                  {{ getLabel(`canPatchFbner`, 'Draft' ) }}
                </v-btn>
                <v-btn
                  v-if="showLabel(`canPostFbner`) && !drafts.length"
                  id="initiate-btn"
                  color="primary"
                  type="button"
                  :loading="loading"
                  @click="$router.replace({params: { operation: 'create'} }); readOnly = false"
                >
                  <v-icon class="mr-2">
                    mdi-file-plus
                  </v-icon>
                  {{ getLabel(`canPostFbner`, 'Initiate FBNER' ) }}
                </v-btn>
              </template>
              <template v-if="operation === 'view' && !isSubmitted && forEvaluation">
                <v-btn
                  v-show="isDraft && showLabel(`canPatchFbner`)"
                  id="edit-btn"
                  color="primary"
                  :loading="loading"
                  @click="$router.replace({params: { operation: 'update'} })"
                >
                  <v-icon class="mr-2">
                    mdi-pencil
                  </v-icon>
                  {{ getLabel(`canPatchFbner`, 'Edit Draft') }}
                </v-btn>
              </template>
              <!-- REVIEWER -->
              <template v-if="operation === 'view' && (initialReviewer || finalReviewer)">
                <v-btn
                  v-show="showLabel(`canPatchFbner`)"
                  id="edit-btn"
                  color="primary"
                  :loading="loading"
                  @click="$router.replace({params: { operation: 'update'} })"
                >
                  <v-icon class="mr-2">
                    mdi-file-search
                  </v-icon>
                  {{ getLabel(`canPatchFbner`, 'Review') }}
                </v-btn>
              </template>
              <!-- FIRST PASS -->
              <template v-if="operation !== 'view' && formItem.isSubmitted && forReview && initialReviewer">
                <v-btn
                  v-if="showLabel(`canPatchFbner`)"
                  id="submit-btn"
                  color="primary"
                  type="button"
                  :loading="loading"
                  @click="approveFbner('Review')"
                >
                  {{ getLabel(`canPatchFbner`, 'Submit FBNER') }}
                </v-btn>
              </template>
              <!-- FINAL -->
              <template v-if="operation !== 'view' && formItem.isSubmitted && finalReviewer">
                <v-btn
                  v-if="showLabel(`canPatchFbner`)"
                  id="submit-btn"
                  color="primary"
                  type="button"
                  :loading="loading"
                  @click="isForEndorsement = true"
                >
                  {{ getLabel(`canPatchFbner`, 'Approve FBNER') }}
                </v-btn>
              </template>
              <!-- ENDORSE -->
              <template v-if="operation !== 'view' && formItem.isSubmitted && (assignedAdjudicator || assignedInvestigator)">
                <v-btn
                  v-if="showLabel(`canPatchFbner`)"
                  id="submit-btn"
                  color="primary"
                  type="button"
                  :loading="loading"
                  @click="isForEndorsement = true"
                >
                  {{ getLabel(`canPatchFbner`, 'Endorse') }}
                </v-btn>
              </template>
              <!-- ADJUDICATION -->
              <template v-if="operation === 'view' && assignedAdjudicator">
                <v-btn
                  v-show="showLabel(`canPatchFbner`)"
                  id="endorse-btn"
                  color="primary"
                  :loading="loading"
                  @click="$router.replace({params: { operation: 'update'} })"
                >
                  {{ getLabel(`canPatchFbner`, 'Update FBNER') }}
                </v-btn>
              </template>
              <!-- INVESTIGATION -->
              <template v-if="operation === 'view' && assignedInvestigator">
                <v-btn
                  v-show="showLabel(`canPatchFbner`)"
                  id="endorse-btn"
                  color="primary"
                  :loading="loading"
                  @click="$router.replace({params: { operation: 'update'} })"
                >
                  {{ getLabel(`canPatchFbner`, 'Investigate') }}
                </v-btn>
              </template>
              <!-- ENFORCEMENT -->
              <template v-if="operation === 'view' && assignedEnforcer">
                <v-btn
                  v-show="showLabel(`canPatchFbner`)"
                  id="endorse-btn"
                  color="primary"
                  :loading="loading"
                  @click="$router.replace({params: { operation: 'update'} })"
                >
                  {{ getLabel(`canPatchFbner`, 'Update FBNER') }}
                </v-btn>
              </template>
              <!-- CLOSING -->
              <template v-if="operation !== 'view' && formItem.isSubmitted && assignedEnforcer">
                <v-btn
                  v-if="showLabel(`canPatchFbner`)"
                  id="submit-btn"
                  color="primary"
                  type="button"
                  :loading="loading"
                  @click="transmitFbner(process)"
                >
                  {{ getLabel(`canPatchFbner`, 'Done') }}
                </v-btn>
              </template>
            </v-row>
          </v-card-text>
        </v-card>
        <template v-if="roleView === 'drafts' ">
          <template v-if="drafts.length">
            <template
              v-for="(draft, idx) in drafts"
            >
              <v-card
                :key="`card-${idx}`"
                hover
                class="py-6 px-6 mb-5"
                @click="isDraft = true; $router.replace({params: {role: 'evaluate', id: draft._id }})"
              >
                <v-card-title :key="`title-${idx}`">
                  Final Breach Notification Evaluation Report Draft {{ idx + 1 }}
                </v-card-title>
                <v-card-subtitle
                  :key="`subtitle-${idx}`"
                >
                  Saved on {{ draft.dateUpdated }}
                </v-card-subtitle>
              </v-card>
            </template>
          </template>
          <template v-if="!drafts.length">
            <v-card>
              <NoDataYet
                class="pt-5"
                :image="implem"
                :message="'No drafts yet.'"
              />
            </v-card>
          </template>
        </template>
      </v-col>
    </v-row>

    <v-btn
      v-if="showFab"
      fab
      fixed
      bottom
      right
      class="primary btn--fab"
      @click="scrollToTop()"
    >
      <v-icon
        color="#FFFFFF"
      >
        mdi-arrow-up
      </v-icon>
    </v-btn>
  </v-container>
</template>

<script>
import { validationMixin } from 'vuelidate'
import { privilegeMixin } from 'session-plugin'
import implem from '@/assets/implem.png'
import FBNERView from './opts/FBNERView'
import FBNERCreate from './opts/FBNERCreate'
import crud from '@/utils/rest/crud'
import fbnerMixin from '@/mixins/fbnerMixin'
import { fbnerEndorse } from '@/utils/validations'
import ViewFilesModal from '@/components/ViewFilesModal'
export default {
  name: 'FinalBreachEvaluationForm',
  components: {
    FBNERView,
    FBNERCreate,
    ViewFilesModal
  },
  mixins: [
    privilegeMixin,
    validationMixin,
    fbnerMixin
  ],
  props: {
    name: {
      type: String,
      default: ''
    },
    mdTables: {
      type: Object,
      default: undefined
    },
    viewOnly: {
      type: Boolean,
      default: false
    },
    parentId: {
      type: String,
      default: undefined
    }
  },
  data: () => ({
    isForEndorsement: false,
    process: '',
    showFab: false,
    implem: implem
  }),
  computed: {
    breadcrumbs () {
      return {
        divider: '>',
        large: true,
        items: [
          {
            text: 'PDBNF',
            exact: true,
            to: { name: 'FinalBreachEvaluationView', params: { role: this.crumbLink, operation: 'view', id: this.id } }

          },
          {
            text: `${this.formItem.dbnNumber} - FBNER ${this.roleView === 'drafts' ? 'Draft' : ''}`
          }
        ]
      }
    },
    pdbnfCollection () {
      return crud('pdbnf')
    },
    id () {
      return this.$route.params.id
    },
    operation () {
      return this.$route.params.operation
    },
    crumbLink () {
      const val = this.forEvaluation ? 'default' : 'evaluate'
      return val
    },
    forEvaluation () {
      const evaluation = this.dbnStatusList.find(x => x.value === 'For Evaluation - FBNER')
      const status = this.currentStatus.value || (this.formItem.status && this.formItem.status.value)
      return status === ((evaluation && evaluation.value))
    },
    forReview () {
      const initialReview = this.dbnStatusList.find(x => x.value === 'Initial Review - FBNER')
      const finalReview = this.dbnStatusList.find(x => x.value === 'Final Review - FBNER')
      const status = this.currentStatus.value || (this.formItem.status && this.formItem.status.value)
      return status === ((initialReview && initialReview.value) || (finalReview && finalReview.value))
    },
    initialReviewer () {
      const initialReview = this.dbnStatusList.find(x => x.value === 'Initial Review - FBNER')
      const status = this.currentStatus.value || (this.formItem.status && this.formItem.status.value)
      const isAssigned = this.$s.user === (this.assignedTo.firstPass && this.assignedTo.firstPass._id)
      return isAssigned && status === (initialReview && initialReview.value)
    },
    finalReviewer () {
      const finalReview = this.dbnStatusList.find(x => x.value === 'Final Review - FBNER')
      const status = this.currentStatus.value || (this.formItem.status && this.formItem.status.value)
      const isAssigned = this.$s.user === (this.assignedTo.final && this.assignedTo.final._id)
      return isAssigned && status === (finalReview && finalReview.value)
    },
    assignedAdjudicator () {
      const adjudication = this.dbnStatusList.find(x => x.value === 'For Adjudication')
      const status = this.currentStatus.value || (this.formItem.status && this.formItem.status.value)
      const isAssigned = this.$s.user === this.formItem.endorserId
      return isAssigned && status === (adjudication && adjudication.value)
    },
    assignedInvestigator () {
      const investigation = this.dbnStatusList.find(x => x.value === 'For Investigation')
      const status = this.currentStatus.value || (this.formItem.status && this.formItem.status.value)
      const isAssigned = this.$s.user === this.formItem.endorserId
      return isAssigned && status === (investigation && investigation.value)
    },
    assignedEnforcer () {
      const enforcement = this.dbnStatusList.find(x => x.value === 'For Enforcement')
      const status = this.currentStatus.value || (this.formItem.status && this.formItem.status.value)
      const isAssigned = this.$s.user === this.formItem.endorserId
      return isAssigned && status === (enforcement && enforcement.value)
    },
    endorseItems () {
      let items = []
      if (this.finalReviewer) items = ['Recipient (Adjudication)', 'Recipient (Investigation)']
      return items
    },
    endorseItemsUsers () {
      if (this.endorsedTo === '') return
      let toFilter = ''
      if (this.endorsedTo === 'Recipient (Adjudication)') toFilter = 'adjudicationRecipient'
      else if (this.endorsedTo === 'Recipient (Investigation)') toFilter = 'investigationRecipient'
      else return
      const filteredUsers = this.users.filter(x => x.userRole.includes(toFilter))
      return filteredUsers
    },
    endorseSecondStageUsers () {
      const status = this.currentStatus.value || (this.formItem.status && this.formItem.status.value)
      const role = status === 'For Adjudication' ? 'enforcementRecipient' : 'adjudicationRecipient'
      const filteredUsers = this.users.filter(x => x.userRole.includes(role))
      return filteredUsers
    },
    nextEndorseStage () {
      let val
      if (this.assignedAdjudicator) {
        val = 'Enforcement'
      } else if (this.assignedEnforcer) {
        val = 'Closing'
      } else if (this.assignedInvestigator) {
        val = 'Adjudication'
      }
      return val
    }

  },
  watch: {
    endorseItemsUsers () {
      if (this.endorsedTo === 'Recipient (Adjudication)') this.process = 'Adjudication'
      else if (this.endorsedTo === 'Recipient (Investigation)') this.process = 'Investigation'
      else this.process = ''
    },
    nextEndorseStage () {
      this.process = this.nextEndorseStage
    }
  },
  mounted () {
    window.addEventListener('scroll', this.onScroll)
  },
  beforeDestroy () {
    window.removeEventListener('scroll', this.onScroll)
  },
  methods: {
    // show confirmation before leaving page
    async back () {
      await this.$store.dispatch('showDialog', {
        title: 'Discard Changes',
        message: 'Changes will not be saved. Are you sure you want to discard?',
        buttonLabel: 'Discard',
        buttonColor: 'red',
        callback: () => {
          this.$router.replace({
            params: {
              operation: 'view'
            }
          })
          this.readOnly = true
        }
      })
    },
    // reset variables related to endorse dialog
    closeEndorseDialog () {
      this.isForEndorsement = false
      this.endorsedTo = ''
      this.endorseToUser = ''
      this.$v.$reset()
    },
    // get value of props for this.isDraft and this.drafts
    showDraft (val, drafts) {
      this.isDraft = val
      this.drafts = drafts
    },
    // trigger confirmDialog of ref="FBNERCreate"
    confirmDialog (submit) {
      this.$refs.FBNERCreate.confirmDialog(submit)
    },
    // trigger approveFbner of ref="FBNERCreate"
    approveFbner (process, user) {
      this.$refs.FBNERCreate.approveFbner(process, user)
    },
    // trigger endorseFbner of ref="FBNERCreate"
    async endorseFbner (process, user) {
      this.loading = true
      await this.$refs.FBNERCreate.endorseFbner(process, user)
      this.loading = false
    },
    // trigger different behavior depending on users
    async transmitFbner (process, user) {
      if (this.assignedEnforcer) {
        this.$store.dispatch('showDialog', {
          title: 'Close FBNER',
          titleColor: 'green',
          message: 'Are you sure you want to close this FBNER?',
          buttonLabel: 'Close',
          buttonColor: 'green',
          callback: () => { this.endorseFbner(process) }
        })
      } else if (!(this.assignedAdjudicator || this.assignedInvestigator)) {
        this.$v.$touch()
        if (!this.$v.$invalid) {
          await this.approveFbner(process, user)
        }
      } else {
        this.$v.endorseToUser.$touch()
        if (!this.$v.endorseToUser.$invalid) {
          this.$store.dispatch('showDialog', {
            title: 'Submit FBNER',
            titleColor: 'green',
            message: `Are you sure you want to endorse this FBNER to ${process}?`,
            buttonLabel: 'Submit',
            buttonColor: 'green',
            callback: () => { this.endorseFbner(process, user) }
          })
        }
      }
    },
    // go to top of page
    scrollToTop () {
      this.$vuetify.goTo(0, 0)
    },
    // show fab after reaching certain scroll height
    onScroll () {
      this.showFab = window.top.scrollY > 200
    }
  },
  validations () {
    return fbnerEndorse
  }

}
</script>

<style scoped>
.v-breadcrumbs >>> a{
  color: #162b54 !important;
  font-weight: bolder;
}
.v-breadcrumbs{
  font-weight: bolder;
}

  .transparent-bg {
    background-color: rgba(0,0,0,0);
  }
</style>
